import { authHeaders } from "./authHeaders";
import { nanoid } from "nanoid";
import { patrolDb, type GuardRecord } from "../lib/db";

type GuardSeed = Omit<GuardRecord, "badgeDataUrl">;
const LOCAL_GUARD_KEY = "customGuardDirectory";

const SEED_GUARDS: GuardSeed[] = [
  {
    id: "G001",
    employeeId: "EMP-1001",
    name: "John Doe",
    email: "john.doe@company.com",
    phone: "(555) 123-4567",
    role: "GUARD",
    status: "ACTIVE",
    lastActive: new Date().toISOString(),
    assignedRoutes: ["Route A", "Route C"],
    hireDate: "2023-01-15",
    emergencyContact: "Jane Doe (555) 123-4568"
  },
  {
    id: "G002",
    employeeId: "EMP-1002",
    name: "Jane Smith",
    email: "jane.smith@company.com",
    phone: "(555) 234-5678",
    role: "GUARD",
    status: "ACTIVE",
    lastActive: new Date(Date.now() - 3600000).toISOString(),
    assignedRoutes: ["Route B"],
    hireDate: "2023-03-20",
    emergencyContact: "John Smith (555) 234-5679"
  },
  {
    id: "S001",
    employeeId: "EMP-2001",
    name: "Mike Johnson",
    email: "mike.johnson@company.com",
    phone: "(555) 345-6789",
    role: "SUPERVISOR",
    status: "ACTIVE",
    lastActive: new Date(Date.now() - 7200000).toISOString(),
    assignedRoutes: [],
    hireDate: "2022-11-10",
    emergencyContact: "Sarah Johnson (555) 345-6790"
  },
  {
    id: "G003",
    employeeId: "EMP-1003",
    name: "Alan Reeves",
    email: "alan.reeves@company.com",
    phone: "(555) 456-7890",
    role: "GUARD",
    status: "DEACTIVATED",
    lastActive: new Date(Date.now() - 86400000 * 7).toISOString(),
    assignedRoutes: [],
    hireDate: "2021-05-02",
    emergencyContact: "Kelly Reeves (555) 456-7891"
  }
];

async function ensureSeedData() {
  await patrolDb.open();
  const guardCount = await patrolDb.guards.count();
  if (guardCount === 0) {
    const seeded = await Promise.all(SEED_GUARDS.map(buildGuardWithBadge));
    await patrolDb.guards.bulkAdd(seeded);
  }
}

export async function fetchGuardDirectory(): Promise<GuardRecord[]> {
  try {
    const apiGuards = await fetchGuardsFromApi();
    return apiGuards;
  } catch (error) {
    console.error("Guard API fetch failed, falling back to local cache", error);
    const local = getLocalGuards();
    try {
      await ensureSeedData();
      const guards = await patrolDb.guards.orderBy("name").toArray();
      for (const guard of guards) {
        if (!guard.badgeDataUrl) {
          guard.badgeDataUrl = generateBadgeUrl(guard.id, guard.name);
          await patrolDb.guards.put(guard);
        }
      }
      return [...guards, ...local].sort((a, b) => a.name.localeCompare(b.name));
    } catch (dexieError) {
      console.error("Dexie fetch failed, returning local guards only", dexieError);
      return local.sort((a, b) => a.name.localeCompare(b.name));
    }
  }
}

export interface CreateGuardInput {
  name: string;
  email: string;
  phone: string;
  employeeId?: string;
  role: GuardRecord["role"];
  emergencyContact: string;
  password: string;
}

export async function createGuardRecord(input: CreateGuardInput): Promise<GuardRecord> {
  try {
    const guard = await createGuardViaApi(input);
    notifyGuardDirectoryUpdated();
    return guard;
  } catch (error) {
    console.error("Guard API creation failed, storing locally", error);
  }
  const fallback = await createGuardOffline(input);
  notifyGuardDirectoryUpdated();
  return fallback;
}

async function buildGuardWithBadge(seed: GuardSeed): Promise<GuardRecord> {
  return {
    ...seed,
    badgeDataUrl: generateBadgeUrl(seed.id, seed.name)
  };
}

function generateBadgeUrl(id: string, name: string) {
  const payload = encodeURIComponent(`GUARD:${id}:${name}`);
  return `https://chart.googleapis.com/chart?cht=qr&chs=320x320&chl=${payload}`;
}

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined" || !("localStorage" in window)) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getLocalGuards(): GuardRecord[] {
  const storage = getLocalStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(LOCAL_GUARD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as GuardRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveGuardLocally(guard: GuardRecord) {
  const storage = getLocalStorage();
  if (!storage) return;
  const guards = getLocalGuards().filter((entry) => entry.id !== guard.id);
  guards.push(guard);
  try {
    storage.setItem(LOCAL_GUARD_KEY, JSON.stringify(guards));
  } catch (error) {
    console.error("Unable to persist guard to localStorage", error);
  }
}

function notifyGuardDirectoryUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("guard-directory-updated"));
}

async function fetchGuardsFromApi(): Promise<GuardRecord[]> {
  const response = await fetch("/api/guards", { headers: authHeaders() });
  if (!response.ok) {
    throw new Error(`Failed to fetch guards (${response.status})`);
  }
  const data = (await response.json()) as { guards: ApiGuard[] };
  return data.guards.map(mapApiGuardToRecord);
}

async function createGuardViaApi(input: CreateGuardInput): Promise<GuardRecord> {
  const response = await fetch("/api/guards", {
    method: "POST",
    headers: authHeaders(true),
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to create guard");
  }
  const data = (await response.json()) as { guard: ApiGuard };
  return mapApiGuardToRecord(data.guard);
}

async function createGuardOffline(input: CreateGuardInput): Promise<GuardRecord> {
  const id = `G-${nanoid(6).toUpperCase()}`;
  const employeeId = input.employeeId && input.employeeId.trim().length > 0 ? input.employeeId.trim() : `EMP-${nanoid(5).toUpperCase()}`;
  const base: GuardSeed = {
    id,
    employeeId,
    name: input.name,
    email: input.email,
    phone: input.phone,
    role: input.role,
    status: "ACTIVE",
    lastActive: new Date().toISOString(),
    assignedRoutes: [],
    hireDate: new Date().toISOString(),
    emergencyContact: input.emergencyContact
  };
  const guard = await buildGuardWithBadge(base);
  try {
    await patrolDb.open();
    await patrolDb.guards.put(guard);
  } catch (error) {
    console.error("Dexie guard write failed", error);
  }
  saveGuardLocally(guard);
  return guard;
}

function mapApiGuardToRecord(guard: ApiGuard): GuardRecord {
  return {
    id: guard.id,
    employeeId: guard.employee_id ?? "",
    name: guard.name,
    email: guard.email,
    phone: guard.phone ?? "",
    role: (guard.role as GuardRecord["role"]) ?? "GUARD",
    status: "ACTIVE",
    lastActive: guard.created_at ?? new Date().toISOString(),
    assignedRoutes: [],
    hireDate: guard.created_at ?? new Date().toISOString(),
    emergencyContact: guard.emergency_contact ?? "",
    badgeDataUrl: guard.badge_svg ?? generateBadgeUrl(guard.id, guard.name)
  };
}

interface ApiGuard {
  id: string;
  employee_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  emergency_contact: string | null;
  badge_svg?: string | null;
  created_at: string | null;
}
